import { MenuProduct } from '../services/MenuService';

// Maryland infused product limit constants
export const MARYLAND_RETAILER_ID = '60e98181-4255-48d4-a3dd-8dde829d0620';
export const INFUSED_SUBCATEGORIES = ['INFUSED_SHAKE', 'INFUSED_PRE_ROLL_PACKS'];
export const INFUSED_LIMIT_GRAMS = 12;

/**
 * Check if the current store is a Maryland store
 */
export const isMarylandStore = (cart: any): boolean => {
  const currentUrl = window.location.pathname;
  // Check URL for Maryland patterns (Olney uses legacy menu IDs 5440/857)
  const isMarylandByUrl = currentUrl.includes('/5440') || currentUrl.includes('/857') || currentUrl.includes('olney');
  const isMarylandByRetailerId = cart?.retailerId === MARYLAND_RETAILER_ID;
  return isMarylandByUrl || isMarylandByRetailerId;
};

/**
 * Check if a product is an infused product subject to the limit
 */
export const isInfusedProduct = (product: MenuProduct | null | undefined): boolean => {
  if (!product) return false;
  return INFUSED_SUBCATEGORIES.includes(product.subcategory || '');
};

/**
 * Calculate the current total flower equivalent of infused products in the cart
 */
export const calculateCartInfusedTotal = (
  cart: any,
  menuProducts: MenuProduct[]
): number => {
  let total = 0;

  cart?.items?.forEach((item: any) => {
    const menuProduct = menuProducts.find(p => p.id === item?.productId);
    if (menuProduct && INFUSED_SUBCATEGORIES.includes(menuProduct.subcategory || '')) {
      const variant = menuProduct.variants?.find(v => v.option === item.option);
      const flowerEquiv = variant?.flowerEquivalent?.value || (menuProduct as any).flowerEquivalent?.value || 0;
      total += flowerEquiv * (item?.quantity || 0);
    }
  });

  return total;
};

/**
 * Check if adding a product would exceed the Maryland infused limit
 * Returns true if limit would be exceeded, false otherwise
 */
export const wouldExceedInfusedLimit = (
  cart: any,
  menuProducts: MenuProduct[],
  product: MenuProduct,
  variant: any,
  quantity: number = 1
): boolean => {
  // Only check for Maryland recreational infused products
  if (!isMarylandStore(cart) ||
      cart?.pricingType !== 'RECREATIONAL' ||
      !isInfusedProduct(product)) {
    return false;
  }

  const currentTotal = calculateCartInfusedTotal(cart, menuProducts);
  const itemFlowerEquiv = variant?.flowerEquivalent?.value || (product as any).flowerEquivalent?.value || 0;
  const newTotal = currentTotal + (itemFlowerEquiv * quantity);

  return newTotal > INFUSED_LIMIT_GRAMS;
};

/**
 * Check if incrementing a cart item would exceed the Maryland infused limit
 */
export const wouldIncrementExceedLimit = (
  cart: any,
  menuProducts: MenuProduct[],
  productId: string,
  currentQuantity: number,
  newQuantity: number
): boolean => {
  // Only check if incrementing
  if (newQuantity <= currentQuantity) {
    return false;
  }

  // Only check for Maryland recreational
  if (!isMarylandStore(cart) || cart?.pricingType !== 'RECREATIONAL') {
    return false;
  }

  // Find the product
  const menuProduct = menuProducts.find(p => p.id === productId);
  if (!menuProduct || !isInfusedProduct(menuProduct)) {
    return false;
  }

  // Find the cart item to get its variant
  const cartItem = cart?.items?.find((item: any) => item.productId === productId);
  if (!cartItem) {
    return false;
  }

  const currentTotal = calculateCartInfusedTotal(cart, menuProducts);
  const variant = menuProduct.variants?.find(v => v.option === cartItem.option);
  const itemFlowerEquiv = variant?.flowerEquivalent?.value || (menuProduct as any).flowerEquivalent?.value || 0;
  const quantityIncrease = newQuantity - currentQuantity;
  const newTotal = currentTotal + (itemFlowerEquiv * quantityIncrease);

  return newTotal > INFUSED_LIMIT_GRAMS;
};
